import PropTypes from "prop-types";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppStore } from "@/store/useAppStore";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Search, HelpCircle, Settings, LogOut, Sparkles, Server, Cloud, ArrowRight, Gift, Menu } from "lucide-react";
import { cn } from "@/lib/utils";
import { startProductTour, resetTourFlag } from "@/components/tour/ProductTour";
import { TrialPill } from "@/components/billing/TrialPill";
import { useTeamStore, useActiveMember } from "@/store/useTeamStore";
import { useAssistantName } from "@/hooks/useAssistantName";
import { useInboundNotifications } from "@/hooks/useInboundNotifications";
import { NotificationCenter } from "@/components/shell/NotificationCenter";

/** Temporary — set false to re-enable instance dropdown + trial sheet. */
const TOPBAR_PILLS_DISABLED = true;

/** Temporary — set false to show Refer a founder in the user menu. */
const USER_MENU_HIDE_REFER_FOUNDER = true;

const pillDisabledClass = "disabled:opacity-100 disabled:cursor-default hover:bg-inherit";

const instanceMeta = {
  "private-saas": { label: "Private SaaS", Icon: Server, hint: "Dedicated to you · $999/mo" },
  "self-hosted":  { label: "Self-hosted",  Icon: Cloud,  hint: "Your cloud, your keys" },
};

function MemberDot({ member, size = 22 }) {
  return <div style={{ width: size, height: size, fontSize: size * 0.4 }} className={cn("rounded-full bg-gradient-to-br text-white font-semibold flex items-center justify-center shrink-0", member.color)}>{member.first?.[0]}{member.name.split(" ")[1]?.[0]}</div>;
}

export function TopBar({ onOpenMobileNav }) {
  const { setPaletteOpen, signOut, auth } = useAppStore();
  const navigate = useNavigate();
  const { instanceType } = useTeamStore();
  const me = useActiveMember();
  const maya = useAssistantName();
  const [panelOpen, setPanelOpen] = useState(false);
  const {
    notifications,
    unreadCount,
    loading,
    refresh,
    openNotification,
    clearOne,
    clearAll,
  } = useInboundNotifications({ enabled: true });

  // Phase A Sprint 1: the current logged-in user identity is sourced from the real
  // /api/auth/me hydration in useAppStore. We keep `me` (mock) for the "switch
  // teammate" submenu only. `currentUser` is what the avatar + name slot renders.
  const realUser = auth?.user;
  const computeInitials = (u) => {
    if (!u) return "?";
    if (u.initials) return u.initials;
    const src = (u.name || u.email || "?").trim();
    const parts = src.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return src.slice(0, 2).toUpperCase();
  };
  const currentUser = realUser
    ? {
        id: realUser.email || "self",
        name: realUser.name || realUser.email || "Loading…",
        first: (realUser.name || realUser.email || "…").split(" ")[0],
        email: realUser.email || "",
        title: realUser.email || "",
        role: "Owner",
        color: "from-indigo-500 to-violet-500",
        initials: computeInitials(realUser),
      }
    : me;
  const inst = instanceMeta[instanceType] || instanceMeta["private-saas"];

  const handleOpenNotification = async (n) => {
    setPanelOpen(false);
    await openNotification(n);
  };

  const handleClearOne = async (id) => {
    try {
      await clearOne(id);
    } catch {
      // keep panel state; next poll will reconcile
    }
  };

  const handleClearAll = async () => {
    try {
      await clearAll();
    } catch {
      // ignore
    }
  };

  return (
    <header className="h-14 shrink-0 border-b border-slate-200 bg-white flex items-center gap-2 sm:gap-3 px-3 sm:px-4 sticky top-0 z-30 min-w-0" data-testid="topbar">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="lg:hidden h-10 w-10 shrink-0"
        onClick={onOpenMobileNav}
        aria-label="Open navigation"
        data-testid="mobile-nav-trigger"
      >
        <Menu size={20} className="text-slate-700" strokeWidth={1.75} />
      </Button>

      {/* Instance pill */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={TOPBAR_PILLS_DISABLED}>
          <button
            type="button"
            disabled={TOPBAR_PILLS_DISABLED}
            className={cn(
              "hidden sm:inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[12px] hover:bg-slate-50 ring-focus shrink-0",
              TOPBAR_PILLS_DISABLED && pillDisabledClass,
            )}
            data-testid="instance-pill"
          >
            <inst.Icon size={13} className="text-indigo-600" />
            <span className="font-medium text-slate-800">{inst.label}</span>
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-72">
          <DropdownMenuLabel className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">Your instance</DropdownMenuLabel>
          <div className="px-2 py-1.5">
            <div className="flex items-center gap-2 rounded-md bg-indigo-50 px-2 py-1.5 ring-1 ring-indigo-200">
              <inst.Icon size={14} className="text-indigo-700" />
              <div className="min-w-0 flex-1">
                <div className="text-[12px] font-semibold text-slate-900">{inst.label}</div>
                <div className="text-[10.5px] text-slate-600">{inst.hint}</div>
              </div>
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            </div>
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate("/settings/instance")} data-testid="instance-manage">
            <Settings size={13} /> Manage instance
            <ArrowRight size={11} className="ml-auto text-slate-400" />
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <div className="hidden sm:block shrink-0">
        <TrialPill disabled={TOPBAR_PILLS_DISABLED} />
      </div>

      {/* Cmd+K search — icon-only on narrow screens */}
      <button
        type="button"
        onClick={() => setPaletteOpen(true)}
        className="sm:hidden h-10 w-10 shrink-0 inline-flex items-center justify-center rounded-md text-slate-500 hover:bg-slate-50 ring-focus"
        data-testid="cmdk-trigger-mobile"
        aria-label={`Ask ${maya} or jump anywhere`}
      >
        <Search size={18} className="text-slate-500" />
      </button>
      <button
        type="button"
        onClick={() => setPaletteOpen(true)}
        className="hidden sm:inline-flex flex-1 min-w-0 max-w-md ml-1 items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm text-slate-500 hover:bg-white hover:border-slate-300 ring-focus"
        data-testid="cmdk-trigger"
      >
        <Search size={14} className="text-slate-400 shrink-0" />
        <span className="flex-1 text-left truncate">Ask {maya} or jump anywhere…</span>
        <kbd className="hidden md:inline-flex items-center gap-0.5 rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-mono text-slate-500 shrink-0">
          <span>⌘</span>
          <span>K</span>
        </kbd>
      </button>

      <div className="flex-1 min-w-0" />

      <div className="flex items-center gap-0.5 sm:gap-1 shrink-0">
      <Button variant="ghost" size="icon" className="h-10 w-10 sm:h-8 sm:w-8" data-testid="help-button" onClick={() => { resetTourFlag(); startProductTour(navigate); }} title="Help & product tour">
        <HelpCircle size={16} className="text-slate-500" strokeWidth={1.75} />
      </Button>

      <NotificationCenter
        open={panelOpen}
        onOpenChange={setPanelOpen}
        notifications={notifications}
        unreadCount={unreadCount}
        loading={loading}
        onOpenNotification={handleOpenNotification}
        onClearOne={handleClearOne}
        onClearAll={handleClearAll}
        onRefresh={refresh}
      />

      {/* User menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" className="flex items-center gap-2 rounded-full ring-focus pl-1 pr-2 hover:bg-slate-50 py-0.5" data-testid="user-menu-trigger">
            <MemberDot member={currentUser} size={28} />
            <div className="hidden md:block text-left leading-tight">
              <div className="text-[11.5px] font-semibold text-slate-900" data-testid="topbar-user-name">{currentUser.first}</div>
              <div className="text-[9.5px] text-slate-500">Working with <span className="text-indigo-700 font-semibold">{maya}</span></div>
            </div>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel className="font-normal">
            <div className="flex items-center gap-2">
              <MemberDot member={currentUser} size={32} />
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-slate-900 truncate" data-testid="topbar-user-fullname">{currentUser.name}</div>
                <div className="text-[10.5px] text-slate-500 truncate" data-testid="topbar-user-email">{currentUser.email || `${currentUser.title} · ${currentUser.role}`}</div>
              </div>
            </div>
            <div className="mt-2 text-[11px] text-slate-500">Working with <span className="text-indigo-700 font-semibold">{maya}</span></div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate("/settings/team")} data-testid="user-menu-settings">
            <Settings size={14} /> Settings
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => { resetTourFlag(); startProductTour(navigate); }} data-testid="restart-tour">
            <Sparkles size={14} /> Restart product tour
          </DropdownMenuItem>
          {!USER_MENU_HIDE_REFER_FOUNDER && (
            <DropdownMenuItem onClick={() => navigate("/settings/referrals")} data-testid="refer-a-founder">
              <Gift size={14} /> Refer a founder
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => { signOut(); navigate("/login"); }} data-testid="user-menu-signout" className="text-rose-600 focus:text-rose-700">
            <LogOut size={14} /> Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      </div>
    </header>
  );
}

TopBar.propTypes = {
  onOpenMobileNav: PropTypes.func,
};

MemberDot.propTypes = {
  member: PropTypes.shape({
    color: PropTypes.string,
    first: PropTypes.string,
    name: PropTypes.string,
  }),
  size: PropTypes.number,
};
